import { useRef, useState, useCallback, useEffect } from "react";
import { Button } from "antd";
import { AudioOutlined, DeleteOutlined, SendOutlined } from "@ant-design/icons";
import { StyledVoiceInput } from "./styles";
import { secondsToTime } from "../../../../../utils/dayjs";
import { useSpeechRecognition } from "../../../../../hooks/useSpeechRecognition";

const VoiceInput = ({ handleSendMessage }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);

  const {
    start: startSpeech,
    stop: stopSpeech,
    interimText,
    getTranscript,
    isSupported,
  } = useSpeechRecognition({ lang: "en-US" });

  const cleanupStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => () => cleanupStream(), [cleanupStream]);

  const startRecording = async () => {
    try {
      setIsRecording(true);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];

      const recorder = new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.start();
      if (isSupported) startSpeech();

      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((t) => t + 1), 1000);
    } catch (err) {
      console.error("Microphone access denied or unavailable:", err);
    }
  };

  const stopRecordingAndSend = () => {
    const recorder = mediaRecorderRef.current;
    if (!recorder) return;

    const text = getTranscript();
    stopSpeech();

    recorder.onstop = () => {
      const audioBlob = new Blob(chunksRef.current, { type: "audio/webm" });
      chunksRef.current = [];

      const audioFile = audioBlob
        ? new File([audioBlob], `voice-${Date.now()}.webm`, {
            type: "audio/webm",
          })
        : null;

      handleSendMessage &&
        handleSendMessage([
          {
            id: `${Date.now()}-voice`,
            file: audioFile,
            filename: audioFile?.name ?? null,
            file_size_bytes: audioFile?.size ?? 0,
            mime_type: audioFile?.type ?? null,
            message: text?.trim() ? `Voice Transcription:\n${text.trim()}` : "",
          },
        ]);

      setIsRecording(false);
      setElapsed(0);
      cleanupStream();
    };
    recorder.stop();
  };

  const cancelRecording = () => {
    const recorder = mediaRecorderRef.current;
    if (!recorder) return;
    stopSpeech();
    recorder.onstop = () => {
      chunksRef.current = [];
    };
    recorder.stop();
    setIsRecording(false);
    setElapsed(0);
    cleanupStream();
  };

  return (
    <StyledVoiceInput className={isRecording ? "voice-input" : ""}>
      {isRecording ? (
        <div className="vi-recording-row">
          <span className="vi-dot" />
          <span className="vi-timer">{secondsToTime(elapsed)}</span>
          <div className="vi-bars">
            {Array.from({ length: 24 }).map((_, i) => (
              <span
                key={i}
                className="vi-bar"
                style={{ animationDelay: `${(i % 6) * 0.12}s` }}
              />
            ))}
          </div>
          {isSupported && interimText && (
            <span className="vi-live-transcript">{interimText}</span>
          )}
          <Button
            type="text"
            shape="circle"
            aria-label="Cancel recording"
            onClick={cancelRecording}
            icon={<DeleteOutlined style={{ fontSize: "20px" }} />}
            danger
          />

          <Button
            type="primary"
            onClick={stopRecordingAndSend}
            shape="circle"
            aria-label="Stop and send recording"
            icon={<SendOutlined />}
          />
        </div>
      ) : (
        <Button
          type="text"
          shape="circle"
          aria-label="Record voice message"
          onClick={startRecording}
          icon={<AudioOutlined />}
          className="icon-btn"
        />
      )}
    </StyledVoiceInput>
  );
};

export default VoiceInput;
